import classNames from "classnames";
import { Immutable } from "immer";
import React from "react";
import { msgid, ngettext, t } from "ttag";

import { constants } from "@reactivated/constants";

import { richT } from "@thelabnyc/thelabui/src/utils/richText";

import { WebPageURL } from "../../models/nominals";
import {
    nearbyStoreSelector,
    preferredLocationSelector,
} from "../../store/common/selectors";
import { useAppSelector } from "../../store/reducers";
import { trackSleepExpertEvent } from "../../utils/analytics";
import { formatPhoneNumber } from "../../utils/format";
import { usePageSetting } from "../../utils/settings";
import { usePageURL } from "../../utils/urls";
import { ChatLink } from "../ChatLink";

import styles from "./index.module.scss";

const AppSlug = constants["tsicommon.types.AppSlug"];

const ContactSupportWidget = (
    props: Immutable<{
        phone: string;
        chatOfflineLink?: WebPageURL;
    }>,
): React.ReactNode => {
    const contactLinkURL = usePageURL("contact_link");
    const homeURL = usePageURL("home");
    const chatOfflineLink = props.chatOfflineLink || contactLinkURL || homeURL;
    const phoneNumber = formatPhoneNumber(props.phone);
    return (
        <div className={styles.contactSupport}>
            <h4 className={styles.heading}>
                <ChatLink
                    chatOnlineText={t`Talk to a Sleep Expert`}
                    chatOfflineText={t`Talk to a Sleep Expert`}
                    chatOfflineLink={chatOfflineLink}
                />
            </h4>
            <span className={styles.copy}>
                {richT(
                    t`<chat>Chat</chat> or call <phone>${phoneNumber}</phone>`,
                    {
                        chat: (children) => (
                            <ChatLink
                                className={styles.link}
                                chatOnlineText={children}
                                chatOfflineText={children}
                                chatOfflineLink={chatOfflineLink}
                            />
                        ),
                        phone: (children) => (
                            <a
                                className={styles.link}
                                href={`tel:${props.phone}`}
                                onClick={() => trackSleepExpertEvent("call")}
                            >
                                {children}
                            </a>
                        ),
                    },
                )}
            </span>
        </div>
    );
};

const NearbyStoresWidget = (): React.ReactNode => {
    // Prevent hydration mismatch: location data is only available client-side
    // after the Redux store is populated, so defer showing it until after hydration.
    const [isHydrated, setIsHydrated] = React.useState(false);
    React.useEffect(() => setIsHydrated(true), []);

    const appSlug = usePageSetting("app_slug");
    const numStores = useAppSelector(
        (state) => nearbyStoreSelector(state).length,
    );
    const location = useAppSelector((state) =>
        preferredLocationSelector(state),
    );
    const getStoreText = (): string => {
        if (appSlug === AppSlug.TEMPURCA) {
            return t`Find a retailer near you`;
        }
        if (!isHydrated) {
            return t`Find stores near you`;
        }
        if (location) {
            return ngettext(
                msgid`${numStores} store near ${location.formatted_address}`,
                `${numStores} stores near ${location.formatted_address}`,
                numStores,
            );
        }
        return ngettext(
            msgid`${numStores} store near you`,
            `${numStores} stores near you`,
            numStores,
        );
    };
    const text = getStoreText();
    const seeListLink =
        appSlug === AppSlug.TEMPURCA ? null : (
            <a
                key="see-retailer-list-link"
                className={styles.link}
                title={t`Find a Retailer`}
                href="/find-a-retailer"
            >
                {t`See List`}
            </a>
        );

    return (
        <div className={styles.nearbyStores}>
            <h4 className={styles.heading}>
                <a
                    title={t`Find a Retailer`}
                    href="/find-a-retailer"
                    className="footer-widget__header"
                >
                    {t`Feel it for Yourself`}
                </a>
            </h4>
            <span className={styles.copy}>
                <span>{text}</span> {seeListLink}
            </span>
        </div>
    );
};

export const FooterWidgets = (
    props: Immutable<{
        phoneNumber: string;
        showContactSupportWidget: boolean;
        showNearbyStoresWidget: boolean;
        chatOfflineLink?: WebPageURL;
    }>,
): React.ReactNode => {
    const firstWidgetClasses = classNames({
        [styles.firstWidget]:
            props.showContactSupportWidget && props.showNearbyStoresWidget,
        [styles.widgetWrapper]: props.showContactSupportWidget,
    });
    if (!props.showContactSupportWidget && !props.showNearbyStoresWidget) {
        return null;
    }
    return (
        <div className={styles.wrapper}>
            <div className={styles.container}>
                {props.showContactSupportWidget && (
                    <div className={firstWidgetClasses}>
                        <ContactSupportWidget
                            phone={props.phoneNumber}
                            chatOfflineLink={props.chatOfflineLink}
                        />
                    </div>
                )}
                {props.showNearbyStoresWidget && (
                    <div className={styles.widgetWrapper}>
                        <NearbyStoresWidget />
                    </div>
                )}
            </div>
        </div>
    );
};
